// src/api.js

import { getAuthToken } from "./AuthContext";

const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL
console.log('URL is '+BACKEND_BASE_URL)

function authHeaders() {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const apiCache = new Map();
const inflightRequests = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000;

async function fetchFromApi(url) {
  const cached = apiCache.get(url);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data;
  }

  if (inflightRequests.has(url)) {
    return inflightRequests.get(url);
  }

  const promise = fetch(url, { headers: authHeaders() })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Errore HTTP: ${response.status} per l'URL ${url}`);
      }
      return response.json();
    })
    .then((data) => {
      apiCache.set(url, { data, timestamp: Date.now() });
      return data;
    })
    .catch((error) => {
      console.error(`Impossibile caricare i dati da ${url}:`, error);
      throw error;
    })
    .finally(() => {
      inflightRequests.delete(url);
    });

  inflightRequests.set(url, promise);
  return promise;
}

export const fetchConfig = () => fetchFromApi("/config.json");

// *** FUNZIONE PER I DETTAGLI DELLE RUN ***
const RUNS_BASE_URL = BACKEND_BASE_URL+"/runs";

/**
 * Recupera la lista delle run (per optimization_global).
 * @param {string} runType
 */
export const fetchRunList = (runType = "optimization_global") =>
  fetchFromApi(`${RUNS_BASE_URL}/details?run_type=${runType}`);

// Funzioni per l'endpoint GLOBAL
const GLOBAL_BASE_URL =
  BACKEND_BASE_URL+"/global";

export const fetchGlobalView = (runId, optimizationMode) => {
  let url = `${GLOBAL_BASE_URL}/view?run_id=${runId}`;
  if (optimizationMode) url += `&optimization_mode=${encodeURIComponent(optimizationMode)}`;
  return fetchFromApi(url);
};
export const fetchGlobalConstraints = () =>
  fetchFromApi(`${GLOBAL_BASE_URL}/constraints`);
export const fetchGlobalForecast = (params = {}) => {
  const defaultParams = {
    page_n: 0,
    page_size: 500,
  };
  const queryParams = { ...defaultParams, ...params };
  const searchParams = new URLSearchParams();
  Object.keys(queryParams).forEach((key) => {
    if (queryParams[key] !== undefined && queryParams[key] !== null) {
      searchParams.append(key, queryParams[key]);
    }
  });
  return fetchFromApi(`${GLOBAL_BASE_URL}/forecast-adj-demand?${searchParams.toString()}`);
};

// *** NUOVE FUNZIONI PER L'ENDPOINT FAMILY ***
const FAMILY_BASE_URL =
  BACKEND_BASE_URL+"/family";

// Funzione per recuperare i dettagli di UNA singola famiglia
export const fetchFamilyDetails = (familyId) =>
  fetchFromApi(
    `${FAMILY_BASE_URL}/details?family=${encodeURIComponent(familyId)}`
  );

export const fetchFamilyDetailsByMode = (optimizationMode) =>
  fetchFromApi(
    `${FAMILY_BASE_URL}/details?optimization_mode=${encodeURIComponent(optimizationMode)}`
  );

/**
 * Recupera la lista paginata delle famiglie con filtri.
 * @param {object} params - Un oggetto contenente i parametri per la query.
 * Es: { page_n: 0, page_size: 50, roi_min: 0.5, sl_max: 0.9 }
 * @returns {Promise<any>} - Una promessa che si risolve con l'oggetto contenente la lista delle famiglie.
 */
export const fetchFamilyList = (params = {}) => {
  // Impostiamo i valori di default per la paginazione se non sono forniti
  const queryParams = {
    page_n: 0,
    page_size: 50,
    ...params,
  };

  // Usiamo URLSearchParams per costruire la stringa di query in modo pulito
  const queryString = new URLSearchParams(queryParams).toString();

  return fetchFromApi(`${FAMILY_BASE_URL}/list?${queryString}`);
};

/**
 * Recupera i dettagli per una lista di famiglie in parallelo.
 * @param {string[]} familyIds - Array di ID delle famiglie (es. ['IAB', 'IAE', ...])
 * @returns {Promise<any[]>} - Una promessa che si risolve con un array di dati delle famiglie.
 */
export const fetchAllFamilyDetails = (familyIds) => {
  const promises = familyIds.map((id) => fetchFamilyDetails(id));
  return Promise.all(promises);
};

const SKU_BASE_URL = BACKEND_BASE_URL+"/sku";

/**
 * Recupera i vincoli per una singola famiglia.
 * @param {string} familyId - L'ID della famiglia.
 * @returns {Promise<any>} - Una promessa che si risolve con i dati dei vincoli.
 */
export const fetchFamilyConstraints = (familyId) =>
  fetchFromApi(
    `${FAMILY_BASE_URL}/constraints?family=${encodeURIComponent(familyId)}`
  );

/**
 * Recupera i dettagli per un singolo SKU.
 * @param {string} skuId - L'ID dello SKU.
 * @param {string} runId - L'ID della run (opzionale).
 * @returns {Promise<any>} - Una promessa che si risolve con i dati dei dettagli.
 */
export const fetchSkuDetails = (skuId, runId) => {
  let url = `${SKU_BASE_URL}/details?sku_id=${encodeURIComponent(skuId)}`;
  if (runId) {
    url += `&run_id=${runId}`;
  }
  return fetchFromApi(url);
};

/**
 * Recupera i vincoli per un singolo SKU.
 * @param {string} skuId - L'ID dello SKU.
 * @returns {Promise<any>} - Una promessa che si risolve con i dati dei vincoli.
 */
export const fetchSkuConstraints = (skuId) =>
  fetchFromApi(
    `${SKU_BASE_URL}/constraints?sku_id=${encodeURIComponent(skuId)}`
  );

/**
 * Recupera la lista paginata degli SKU con filtri e paginazione.
 * @param {object} params - Un oggetto contenente i parametri per la query.
 * Es: { page_n: 0, page_size: 50, lq_min: 10, roi_max: 0.5 }
 * @returns {Promise<any>}
 */
export const fetchSkuList = (params = {}) => {
  // Impostiamo i valori di default per la paginazione se non sono forniti
  const queryParams = {
    page_n: 0,
    page_size: 50,
    ...params,
  };

  // Usiamo URLSearchParams per costruire la stringa di query in modo pulito
  const queryString = new URLSearchParams(queryParams).toString();

  return fetchFromApi(`${SKU_BASE_URL}/list?${queryString}`);
};

/**
 * Recupera i dati di forecast per un singolo SKU.
 * @param {string} skuId - L'ID dello SKU.
 * @param {string} runId - L'ID della run di forecast.
 * @returns {Promise<any>} - Una promessa che si risolve con i dati del forecast.
 */
export const fetchSkuForecast = (skuId, params = {}) => {
  const defaultParams = {
    page_n: 0,
    page_size: 3000,
  };

  const queryParams = { ...defaultParams, ...params };
  const searchParams = new URLSearchParams();

  searchParams.append("sku_id", skuId);

  Object.keys(queryParams).forEach((key) => {
    if (queryParams[key] !== undefined && queryParams[key] !== null) {
      searchParams.append(key, queryParams[key]);
    }
  });

  return fetchFromApi(`${SKU_BASE_URL}/forecast-adj-demand?${searchParams.toString()}`);
};

/**
 * Recupera i dati di forecast per una singola famiglia.
 * @param {string} familyId - L'ID della famiglia.
 * @returns {Promise<any>} - Una promessa che si risolve con i dati del forecast.
 */
export const fetchFamilyForecast = (familyId, params = {}) => {
  const defaultParams = {
    page_n: 0,
    page_size: 3000,
  };

  const queryParams = { ...defaultParams, ...params };
  const searchParams = new URLSearchParams();

  Object.keys(queryParams).forEach((key) => {
    if (queryParams[key] !== undefined && queryParams[key] !== null) {
      searchParams.append(key, queryParams[key]);
    }
  });

  return fetchFromApi(
    `${FAMILY_BASE_URL}/forecast-adj-demand?family=${encodeURIComponent(
      familyId
    )}&${searchParams.toString()}`
  );
};

// *** WIN-RATE FORECAST FUNCTIONS ***

export const fetchGlobalWinrate = (params = {}) => {
  const defaultParams = {
    page_n: 0,
    page_size: 500,
  };
  const queryParams = { ...defaultParams, ...params };
  const searchParams = new URLSearchParams();
  Object.keys(queryParams).forEach((key) => {
    if (queryParams[key] !== undefined && queryParams[key] !== null) {
      searchParams.append(key, queryParams[key]);
    }
  });
  return fetchFromApi(`${GLOBAL_BASE_URL}/forecast-winrate?${searchParams.toString()}`);
};

export const fetchFamilyWinrate = (familyId, params = {}) => {
  const defaultParams = {
    page_n: 0,
    page_size: 3000,
  };
  const queryParams = { ...defaultParams, ...params };
  const searchParams = new URLSearchParams();
  Object.keys(queryParams).forEach((key) => {
    if (queryParams[key] !== undefined && queryParams[key] !== null) {
      searchParams.append(key, queryParams[key]);
    }
  });
  return fetchFromApi(
    `${FAMILY_BASE_URL}/forecast-winrate?family=${encodeURIComponent(
      familyId
    )}&${searchParams.toString()}`
  );
};

export const fetchSkuWinrate = (skuId, params = {}) => {
  const defaultParams = {
    page_n: 0,
    page_size: 3000,
  };
  const queryParams = { ...defaultParams, ...params };
  const searchParams = new URLSearchParams();
  searchParams.append("sku_id", skuId);
  Object.keys(queryParams).forEach((key) => {
    if (queryParams[key] !== undefined && queryParams[key] !== null) {
      searchParams.append(key, queryParams[key]);
    }
  });
  return fetchFromApi(`${SKU_BASE_URL}/forecast-winrate?${searchParams.toString()}`);
};

export const downloadGlobalWinrate = async (
  params = {},
  filename = "global_winrate.xlsx"
) => {
  const cleanParams = { ...params };
  delete cleanParams.page_n;
  delete cleanParams.page_size;

  const searchParams = new URLSearchParams();
  searchParams.append("download", "true");
  searchParams.append("_", Date.now());

  Object.keys(cleanParams).forEach((key) => {
    if (cleanParams[key] !== undefined && cleanParams[key] !== null) {
      searchParams.append(key, cleanParams[key]);
    }
  });

  const url = `${GLOBAL_BASE_URL}/forecast-winrate?${searchParams.toString()}`;

  try {
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(`Errore Server: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download Winrate Error:", error);
    throw error;
  }
};

export const downloadAllSkusWinrate = async (
  params = {},
  filename = "all_skus_winrate.xlsx"
) => {
  const cleanParams = { ...params };
  delete cleanParams.page_n;
  delete cleanParams.page_size;

  const searchParams = new URLSearchParams();
  searchParams.append("download", "true");
  searchParams.append("_", Date.now());

  Object.keys(cleanParams).forEach((key) => {
    if (cleanParams[key] !== undefined && cleanParams[key] !== null) {
      searchParams.append(key, cleanParams[key]);
    }
  });

  const url = `${SKU_BASE_URL}/forecast-winrate/list?${searchParams.toString()}`;

  try {
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(`Errore Server: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download All SKUs Winrate Error:", error);
    throw error;
  }
};

export const downloadFamilyWinrate = async (
  familyId,
  params = {},
  filename = "family_winrate.xlsx"
) => {
  const cleanParams = { ...params };
  delete cleanParams.page_n;
  delete cleanParams.page_size;

  const searchParams = new URLSearchParams();
  searchParams.append("download", "true");
  searchParams.append("_", Date.now());

  Object.keys(cleanParams).forEach((key) => {
    if (cleanParams[key] !== undefined && cleanParams[key] !== null) {
      searchParams.append(key, cleanParams[key]);
    }
  });

  const url = `${FAMILY_BASE_URL}/forecast-winrate?family=${encodeURIComponent(
    familyId
  )}&${searchParams.toString()}`;

  try {
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(`Errore Server: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download Family Winrate Error:", error);
    throw error;
  }
};

export const downloadSkuWinrate = async (
  skuId,
  params = {},
  filename = "sku_winrate.xlsx"
) => {
  const cleanParams = { ...params };
  delete cleanParams.page_n;
  delete cleanParams.page_size;

  const searchParams = new URLSearchParams();
  searchParams.append("download", "true");
  searchParams.append("_", Date.now());
  searchParams.append("sku_id", skuId);

  Object.keys(cleanParams).forEach((key) => {
    if (cleanParams[key] !== undefined && cleanParams[key] !== null) {
      searchParams.append(key, cleanParams[key]);
    }
  });

  const url = `${SKU_BASE_URL}/forecast-winrate?${searchParams.toString()}`;

  try {
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(`Errore Server: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download SKU Winrate Error:", error);
    throw error;
  }
};

// *** NUOVE FUNZIONI PER L'ENDPOINT PRODUCT DATA ***
const PRODUCT_DATA_BASE_URL =
  BACKEND_BASE_URL+"/product_data";

/**
 * Recupera i dati prodotti paginati con nomi fornitori.
 * @param {object} params - Parametri (page_n, page_size, order_by, order, status)
 */
export const fetchProductData = (params = {}) => {
  const defaultParams = {
    page_n: 0,
    page_size: 50,
  };

  const queryParams = { ...defaultParams, ...params };
  const searchParams = new URLSearchParams();

  Object.keys(queryParams).forEach((key) => {
    if (
      queryParams[key] !== undefined &&
      queryParams[key] !== null &&
      queryParams[key] !== ""
    ) {
      searchParams.append(key, queryParams[key]);
    }
  });

  return fetchFromApi(`${PRODUCT_DATA_BASE_URL}?${searchParams.toString()}`);
};

const SKU_LT_BASE_URL =
  BACKEND_BASE_URL+"/sku_lt";

export const fetchSkuLtData = (params = {}) => {
  const defaultParams = {
    page_n: 0,
    page_size: 50,
    order_by: "SKU DESCRIPTION",
    order: "asc",
  };

  const queryParams = { ...defaultParams, ...params };
  const searchParams = new URLSearchParams();

  Object.keys(queryParams).forEach((key) => {
    if (
      queryParams[key] !== undefined &&
      queryParams[key] !== null &&
      queryParams[key] !== ""
    ) {
      searchParams.append(key, queryParams[key]);
    }
  });

  return fetchFromApi(`${SKU_LT_BASE_URL}?${searchParams.toString()}`);
};

const SUPPLIERS_BASE_URL =
  BACKEND_BASE_URL+"/suppliers";

export const fetchSuppliersData = (params = {}) => {
  const defaultParams = {
    page_n: 0,
    page_size: 50,
  };

  const queryParams = { ...defaultParams, ...params };
  const searchParams = new URLSearchParams();

  Object.keys(queryParams).forEach((key) => {
    if (
      queryParams[key] !== undefined &&
      queryParams[key] !== null &&
      queryParams[key] !== ""
    ) {
      searchParams.append(key, queryParams[key]);
    }
  });

  return fetchFromApi(`${SUPPLIERS_BASE_URL}?${searchParams.toString()}`);
};

const QUOTATIONS_DEMAND_BASE_URL =
  BACKEND_BASE_URL+"/quotations_demand";

export const fetchQuotationsDemandData = (params = {}) => {
  const defaultParams = {
    page_n: 0,
    page_size: 50,
  };

  const queryParams = { ...defaultParams, ...params };
  const searchParams = new URLSearchParams();

  Object.keys(queryParams).forEach((key) => {
    if (
      queryParams[key] !== undefined &&
      queryParams[key] !== null &&
      queryParams[key] !== ""
    ) {
      searchParams.append(key, queryParams[key]);
    }
  });

  return fetchFromApi(
    `${QUOTATIONS_DEMAND_BASE_URL}?${searchParams.toString()}`
  );
};

const BOOKINGS_BASE_URL =
  BACKEND_BASE_URL+"/bookings";

export const fetchBookingsData = (params = {}) => {
  const defaultParams = {
    page_n: 0,
    page_size: 50,
  };

  const queryParams = { ...defaultParams, ...params };
  const searchParams = new URLSearchParams();

  Object.keys(queryParams).forEach((key) => {
    if (
      queryParams[key] !== undefined &&
      queryParams[key] !== null &&
      queryParams[key] !== ""
    ) {
      searchParams.append(key, queryParams[key]);
    }
  });

  return fetchFromApi(`${BOOKINGS_BASE_URL}?${searchParams.toString()}`);
};

const INVENTORY_SNAPSHOT_BASE_URL =
  BACKEND_BASE_URL+"/inventory_snapshot";

export const fetchInventorySnapshotData = (params = {}) => {
  const defaultParams = {
    page_n: 0,
    page_size: 50,
  };

  const queryParams = { ...defaultParams, ...params };
  const searchParams = new URLSearchParams();

  Object.keys(queryParams).forEach((key) => {
    if (
      queryParams[key] !== undefined &&
      queryParams[key] !== null &&
      queryParams[key] !== ""
    ) {
      searchParams.append(key, queryParams[key]);
    }
  });

  return fetchFromApi(
    `${INVENTORY_SNAPSHOT_BASE_URL}?${searchParams.toString()}`
  );
};

const PURCHASE_ORDERS_BASE_URL =
  BACKEND_BASE_URL+"/purchase_orders";

export const fetchPurchaseOrdersData = (params = {}) => {
  const defaultParams = {
    page_n: 0,
    page_size: 50,
  };

  const queryParams = { ...defaultParams, ...params };
  const searchParams = new URLSearchParams();

  Object.keys(queryParams).forEach((key) => {
    if (
      queryParams[key] !== undefined &&
      queryParams[key] !== null &&
      queryParams[key] !== ""
    ) {
      searchParams.append(key, queryParams[key]);
    }
  });

  return fetchFromApi(`${PURCHASE_ORDERS_BASE_URL}?${searchParams.toString()}`);
};

/**
 * Scarica la lista delle famiglie in formato CSV supportando lo streaming.
 * @param {object} params - I filtri correnti applicati.
 * @param {string} filename - Il nome del file da salvare (default: family_overview.csv).
 */
export const downloadFamilyList = async (
  params = {},
  filename = "family_overview.xlsx"
) => {
  // 1. Pulizia dei parametri di paginazione (non servono per il download completo)
  const cleanParams = { ...params };
  delete cleanParams.page_n;
  delete cleanParams.page_size;

  // 2. Costruzione della Query String
  // Il backend si aspetta parametri singoli (es. roi_min, run_id), non un oggetto JSON.
  const searchParams = new URLSearchParams();

  searchParams.append("download", "true");
  searchParams.append("_", Date.now()); // Cache buster

  // Aggiungiamo ogni filtro come parametro singolo
  Object.keys(cleanParams).forEach((key) => {
    if (
      cleanParams[key] !== undefined &&
      cleanParams[key] !== null &&
      cleanParams[key] !== ""
    ) {
      searchParams.append(key, cleanParams[key]);
    }
  });

  const url = `${FAMILY_BASE_URL}/list?${searchParams.toString()}`;

  try {
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(`Errore Server: ${response.status}`);
    }

    // 3. Gestione della risposta come BLOB (Binary Large Object) per Excel
    const blob = await response.blob();

    // Creazione del link temporaneo per il download
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename; // Il browser userà questo nome
    document.body.appendChild(a);
    a.click();

    // Pulizia
    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download Error:", error);
    throw error;
  }
};

/**
 * Scarica la lista degli SKU in formato Excel/CSV supportando lo streaming.
 * @param {object} params - I filtri correnti applicati.
 * @param {string} filename - Il nome del file da salvare.
 */
export const downloadSkuList = async (
  params = {},
  filename = "sku_overview.xlsx"
) => {
  // 1. Pulizia dei parametri di paginazione
  const cleanParams = { ...params };
  delete cleanParams.page_n;
  delete cleanParams.page_size;

  // 2. Costruzione della Query String
  const searchParams = new URLSearchParams();

  searchParams.append("download", "true");
  searchParams.append("_", Date.now()); // Cache buster

  Object.keys(cleanParams).forEach((key) => {
    if (
      cleanParams[key] !== undefined &&
      cleanParams[key] !== null &&
      cleanParams[key] !== ""
    ) {
      searchParams.append(key, cleanParams[key]);
    }
  });

  const url = `${SKU_BASE_URL}/list?${searchParams.toString()}`;

  try {
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(`Errore Server: ${response.status}`);
    }

    // 3. Gestione della risposta come BLOB
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    // Pulizia
    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download SKU Error:", error);
    throw error;
  }
};

/**
 * Scarica il Global Forecast in formato Excel/CSV.
 * @param {object} params - Deve contenere { run_id: ... }
 * @param {string} filename - Nome del file
 */
export const downloadGlobalForecast = async (
  params = {},
  filename = "global_forecast_adj_demand.xlsx"
) => {
  // 1. Pulizia parametri (paginazione non serve per il download)
  const cleanParams = { ...params };
  delete cleanParams.page_n;
  delete cleanParams.page_size;

  // 2. Costruzione Query String
  const searchParams = new URLSearchParams();
  searchParams.append("download", "true");
  searchParams.append("_", Date.now());

  Object.keys(cleanParams).forEach((key) => {
    if (cleanParams[key] !== undefined && cleanParams[key] !== null) {
      searchParams.append(key, cleanParams[key]);
    }
  });

  const url = `${GLOBAL_BASE_URL}/forecast-adj-demand?${searchParams.toString()}`;

  try {
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(`Errore Server: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download Forecast Error:", error);
    throw error;
  }
};

/**
 * Scarica il Family Forecast in formato Excel/CSV.
 * @param {string} familyId - L'ID della famiglia.
 * @param {object} params - Deve contenere { run_id: ... }
 * @param {string} filename - Nome del file
 */
export const downloadFamilyForecast = async (
  familyId,
  params = {},
  filename = "family_forecast.xlsx"
) => {
  // 1. Pulizia parametri
  const cleanParams = { ...params };
  delete cleanParams.page_n;
  delete cleanParams.page_size;

  // 2. Costruzione Query String
  const searchParams = new URLSearchParams();
  searchParams.append("download", "true");
  searchParams.append("_", Date.now());

  Object.keys(cleanParams).forEach((key) => {
    if (cleanParams[key] !== undefined && cleanParams[key] !== null) {
      searchParams.append(key, cleanParams[key]);
    }
  });

  // Costruiamo l'URL includendo il familyId
  const url = `${FAMILY_BASE_URL}/forecast-adj-demand?family=${encodeURIComponent(
    familyId
  )}&${searchParams.toString()}`;

  try {
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(`Errore Server: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download Family Forecast Error:", error);
    throw error;
  }
};

/**
 * Scarica il Forecast di un singolo SKU in formato Excel/CSV.
 * @param {string} skuId - L'ID dello SKU.
 * @param {object} params - Deve contenere { run_id: ... }
 * @param {string} filename - Nome del file
 */
export const downloadSkuForecast = async (
  skuId,
  params = {},
  filename = "sku_forecast.xlsx"
) => {
  // 1. Pulizia parametri
  const cleanParams = { ...params };
  delete cleanParams.page_n;
  delete cleanParams.page_size;

  // 2. Costruzione Query String
  const searchParams = new URLSearchParams();
  searchParams.append("download", "true");
  searchParams.append("_", Date.now());

  // Aggiungiamo l'ID SKU obbligatorio
  searchParams.append("sku_id", skuId);

  Object.keys(cleanParams).forEach((key) => {
    if (cleanParams[key] !== undefined && cleanParams[key] !== null) {
      searchParams.append(key, cleanParams[key]);
    }
  });

  const url = `${SKU_BASE_URL}/forecast-adj-demand?${searchParams.toString()}`;

  try {
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(`Errore Server: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download SKU Forecast Error:", error);
    throw error;
  }
};

/**
 * Scarica il forecast di TUTTE le SKU (endpoint /sku/forecast/list).
 * @param {object} params - Deve contenere { run_id: ... }
 * @param {string} filename - Nome del file
 */
export const downloadAllSkusForecast = async (
  params = {},
  filename = "all_skus_forecast.xlsx"
) => {
  const cleanParams = { ...params };
  delete cleanParams.page_n;
  delete cleanParams.page_size;

  const searchParams = new URLSearchParams();
  searchParams.append("download", "true");
  searchParams.append("_", Date.now());

  Object.keys(cleanParams).forEach((key) => {
    if (cleanParams[key] !== undefined && cleanParams[key] !== null) {
      searchParams.append(key, cleanParams[key]);
    }
  });

  const url = `${SKU_BASE_URL}/forecast-adj-demand/list?${searchParams.toString()}`;

  try {
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(`Errore Server: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download All SKUs Forecast Error:", error);
    throw error;
  }
};

const ALERTS_BASE_URL =
  BACKEND_BASE_URL+"/alerts";

export const fetchAlertsData = (params = {}) => {
  const defaultParams = {};

  const queryParams = { ...defaultParams, ...params };
  const searchParams = new URLSearchParams();

  Object.keys(queryParams).forEach((key) => {
    if (
      queryParams[key] !== undefined &&
      queryParams[key] !== null &&
      queryParams[key] !== ""
    ) {
      searchParams.append(key, queryParams[key]);
    }
  });

  return fetchFromApi(`${ALERTS_BASE_URL}?${searchParams.toString()}`);
};

export const downloadProductData = async (
  params = {},
  filename = "master_data.xlsx"
) => {
  const cleanParams = { ...params };
  delete cleanParams.page_n;
  delete cleanParams.page_size;

  const searchParams = new URLSearchParams();
  searchParams.append("download", "true");
  searchParams.append("_", Date.now());

  Object.keys(cleanParams).forEach((key) => {
    if (
      cleanParams[key] !== undefined &&
      cleanParams[key] !== null &&
      cleanParams[key] !== ""
    ) {
      searchParams.append(key, cleanParams[key]);
    }
  });

  const BASE_URL =
    BACKEND_BASE_URL+"/product_data";
  const url = `${BASE_URL}?${searchParams.toString()}`;

  try {
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(`Errore Server: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download Product Data Error:", error);
    throw error;
  }
};

export const downloadSkuLtData = async (
  params = {},
  filename = "sku_lt_data.xlsx"
) => {
  const cleanParams = { ...params };
  delete cleanParams.page_n;
  delete cleanParams.page_size;

  const searchParams = new URLSearchParams();
  searchParams.append("download", "true");
  searchParams.append("_", Date.now());

  Object.keys(cleanParams).forEach((key) => {
    if (
      cleanParams[key] !== undefined &&
      cleanParams[key] !== null &&
      cleanParams[key] !== ""
    ) {
      searchParams.append(key, cleanParams[key]);
    }
  });

  const SKU_LT_BASE_URL =
    BACKEND_BASE_URL+"/sku_lt";
  const url = `${SKU_LT_BASE_URL}?${searchParams.toString()}`;

  try {
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(`Errore Server: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download SKU LT Error:", error);
    throw error;
  }
};

export const downloadSuppliersData = async (
  params = {},
  filename = "suppliers_data.xlsx"
) => {
  const cleanParams = { ...params };
  delete cleanParams.page_n;
  delete cleanParams.page_size;

  const searchParams = new URLSearchParams();
  searchParams.append("download", "true");
  searchParams.append("_", Date.now());

  Object.keys(cleanParams).forEach((key) => {
    if (
      cleanParams[key] !== undefined &&
      cleanParams[key] !== null &&
      cleanParams[key] !== ""
    ) {
      searchParams.append(key, cleanParams[key]);
    }
  });

  const SUPPLIERS_BASE_URL =
    BACKEND_BASE_URL+"/suppliers";
  const url = `${SUPPLIERS_BASE_URL}?${searchParams.toString()}`;

  try {
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(`Errore Server: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download Suppliers Error:", error);
    throw error;
  }
};

export const downloadQuotationsDemandData = async (
  params = {},
  filename = "quotations_demand_data.xlsx"
) => {
  const cleanParams = { ...params };
  delete cleanParams.page_n;
  delete cleanParams.page_size;

  const searchParams = new URLSearchParams();
  searchParams.append("download", "true");
  searchParams.append("_", Date.now());

  Object.keys(cleanParams).forEach((key) => {
    if (
      cleanParams[key] !== undefined &&
      cleanParams[key] !== null &&
      cleanParams[key] !== ""
    ) {
      searchParams.append(key, cleanParams[key]);
    }
  });

  const QUOTATIONS_DEMAND_BASE_URL =
    BACKEND_BASE_URL+"/quotations_demand";
  const url = `${QUOTATIONS_DEMAND_BASE_URL}?${searchParams.toString()}`;

  try {
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(`Errore Server: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download Quotations Error:", error);
    throw error;
  }
};

export const downloadBookingsData = async (
  params = {},
  filename = "bookings_data.xlsx"
) => {
  const cleanParams = { ...params };
  delete cleanParams.page_n;
  delete cleanParams.page_size;

  const searchParams = new URLSearchParams();
  searchParams.append("download", "true");
  searchParams.append("_", Date.now());

  Object.keys(cleanParams).forEach((key) => {
    if (
      cleanParams[key] !== undefined &&
      cleanParams[key] !== null &&
      cleanParams[key] !== ""
    ) {
      searchParams.append(key, cleanParams[key]);
    }
  });

  const BOOKINGS_BASE_URL =
    BACKEND_BASE_URL+"/bookings";
  const url = `${BOOKINGS_BASE_URL}?${searchParams.toString()}`;

  try {
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(`Errore Server: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download Bookings Error:", error);
    throw error;
  }
};

export const downloadInventorySnapshotData = async (
  params = {},
  filename = "inventory_snapshot.xlsx"
) => {
  const cleanParams = { ...params };
  delete cleanParams.page_n;
  delete cleanParams.page_size;

  const searchParams = new URLSearchParams();
  searchParams.append("download", "true");
  searchParams.append("_", Date.now());

  Object.keys(cleanParams).forEach((key) => {
    if (
      cleanParams[key] !== undefined &&
      cleanParams[key] !== null &&
      cleanParams[key] !== ""
    ) {
      searchParams.append(key, cleanParams[key]);
    }
  });

  const INVENTORY_SNAPSHOT_BASE_URL =
    BACKEND_BASE_URL+"/inventory_snapshot";
  const url = `${INVENTORY_SNAPSHOT_BASE_URL}?${searchParams.toString()}`;

  try {
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(`Errore Server: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download Inventory Snapshot Error:", error);
    throw error;
  }
};

export const downloadPurchaseOrdersData = async (
  params = {},
  filename = "purchase_orders.xlsx"
) => {
  const cleanParams = { ...params };
  delete cleanParams.page_n;
  delete cleanParams.page_size;

  const searchParams = new URLSearchParams();
  searchParams.append("download", "true");
  searchParams.append("_", Date.now());

  Object.keys(cleanParams).forEach((key) => {
    if (
      cleanParams[key] !== undefined &&
      cleanParams[key] !== null &&
      cleanParams[key] !== ""
    ) {
      searchParams.append(key, cleanParams[key]);
    }
  });

  const PURCHASE_ORDERS_BASE_URL =
    BACKEND_BASE_URL+"/purchase_orders";
  const url = `${PURCHASE_ORDERS_BASE_URL}?${searchParams.toString()}`;

  try {
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error(`Errore Server: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download Purchase Orders Error:", error);
    throw error;
  }
};

/**
 * Recupera una lista di SKU simili basandosi su una sottostringa.
 * @param {string} substring - Il testo parziale digitato dall'utente.
 * @returns {Promise<any>} - Oggetto contenente l'array "skus".
 */
export const fetchSimilarSkus = (substring) =>
  fetchFromApi(
    `${SKU_BASE_URL}/similar?substring=${encodeURIComponent(substring)}`
  );
